import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import propTypes from 'prop-types';
import InfoTip from '../../InfoTip';
import styles from './LineChart.module.scss';
import { getDefaultChartColors } from '../chart-colors';
import { tooltipConfig, gridConfig, animationConfig } from '../chart-config';

ChartJS.register(
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

// Mirror of BarChart's API/markup so the redesigned dashboard cards stay
// visually consistent. chartData shape:
//   {
//     labels: string[],
//     lineTitle: string[],          // one per series, used in legend + tooltip
//     borderColors?: string[],       // stroke per series; falls back to palette
//     data: number[][],              // one array per series, parallel to lineTitle
//     dashed?: boolean[],            // optional: render a series as dashed (prior period)
//   }
const LineChart = ({ title, header, info, legendConfig = { display: false }, chartData }) => {
  const { labels, lineTitle, borderColors, data, dashed } = chartData;

  const palette = getDefaultChartColors();
  const datasets = data.map((series, index) => {
    const stroke = borderColors?.[index] || palette[index % palette.length];
    return {
      label: lineTitle[index],
      data: series,
      borderColor: stroke,
      // Subtle area below the primary series; mute prior-period (dashed) so
      // it reads as a reference, not a competing series.
      backgroundColor: dashed?.[index] ? 'rgba(0,0,0,0)' : `${stroke}1F`, // ~12% alpha
      fill: dashed?.[index] ? false : 'origin',
      borderDash: dashed?.[index] ? [4, 4] : [],
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: stroke,
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      tension: 0.35,
      spanGaps: true,
    };
  });

  const preparedChartData = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: legendConfig,
      tooltip: tooltipConfig,
      datalabels: { display: false },
    },
    scales: {
      x: gridConfig.x,
      y: gridConfig.y,
    },
    animation: animationConfig,
  };

  return (
    <div className={styles.chartCard}>
      <div className={styles.lineChartShell}>
        <div className={styles.chartHeader}>
          <span className={styles.chartTitle}>
            {title}
            {info && <InfoTip content={info} label={`About ${title}`} />}
          </span>
          {header ? (
            <div className={styles.salesInfo}>
              <h2>
                {`${header.value} `}
                {header.difference ? (
                  <span
                    className={
                      styles.percentage +
                      (header.difference.startsWith('-') ? ` ${styles.negative}` : '')
                    }
                  >
                    {header.difference}
                  </span>
                ) : null}
              </h2>
              {header.label ? <p>{header.label}</p> : null}
            </div>
          ) : null}
        </div>
        <div className={styles.lineChart}>
          <Line data={preparedChartData} options={options} />
        </div>
      </div>
    </div>
  );
};

LineChart.propTypes = {
  title: propTypes.string,
  header: propTypes.object,
  info: propTypes.node,
  legendConfig: propTypes.object,
  chartData: propTypes.object,
};

export default LineChart;
